/**
 * Landing Page Component - Figma Design
 * Professional landing page for StepFlow AI application
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { canAccessNetworkWorkbench } from '@/utils/stepflowRoles';
import { APP_BROWSE_DEFAULT_HOME } from '@stepflow/ui-shared/constants/appBrowseFlags.js';
import { ArrowRight, Play, MessageSquare, GitBranch, Users, FolderInput, Brain, UserCheck, Clock, Zap } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, workspaceRole } = useAuth();

  // Redirect to app if already logged in. Network-workbench roles land on the
  // Apps browse home when the flag is on (else the classic create flow).
  useEffect(() => {
    if (user) {
      const workbenchHome = APP_BROWSE_DEFAULT_HOME ? '/registry' : '/network/new';
      navigate(canAccessNetworkWorkbench(workspaceRole) ? workbenchHome : '/agentic');
    }
  }, [user, workspaceRole, navigate]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-2 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                StepFlow AI
              </span>
            </div>
            
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('features')} className="text-gray-600 hover:text-gray-900 transition-colors">
                Features
              </button>
              <button onClick={() => scrollToSection('capabilities')} className="text-gray-600 hover:text-gray-900 transition-colors">
                Capabilities
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="text-gray-600 hover:text-gray-900 transition-colors">
                How It Works
              </button>
            </nav>

            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all"
            >
              Login
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
          <div className="container mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="inline-block">
                  <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium">
                    Prompt-Driven Workflow Automation
                  </span>
                </div>
                
                <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                  Build Intelligent{' '}
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Agentic Networks
                  </span>
                </h1>
                
                <p className="text-xl text-gray-600 leading-relaxed">
                  Transform your ideas into production-ready intelligent workflows with AI-powered prompt-driven development. 
                  No complex coding required—just describe what you need.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => navigate('/login')}
                    className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg font-semibold rounded-lg transition-all"
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </button>
                  <button className="inline-flex items-center justify-center px-8 py-4 border-2 border-gray-300 hover:border-gray-400 text-gray-700 text-lg font-medium rounded-lg transition-all">
                    <Play className="mr-2 w-5 h-5" />
                    Watch Demo
                  </button>
                </div>
                
                <div className="flex items-center gap-8 pt-4">
                  <div>
                    <div className="text-3xl font-bold text-gray-900">10K+</div>
                    <div className="text-sm text-gray-600">Workflows Created</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">99.9%</div>
                    <div className="text-sm text-gray-600">Uptime SLA</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">24/7</div>
                    <div className="text-sm text-gray-600">AI Processing</div>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur-3xl opacity-20"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="/images/photo-1.jpeg" 
                    alt="Workflow Network Visualization"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Features Section */}
        <section id="features" className="py-20 px-6 bg-white">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">
                Powerful Features at Your Fingertips
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Everything you need to build sophisticated workflows without the complexity
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: MessageSquare,
                  title: 'Prompt-Driven',
                  description: 'Describe your workflow in natural language and watch it come to life',
                  gradient: 'from-blue-500 to-cyan-500'
                },
                {
                  icon: GitBranch,
                  title: 'State Machines',
                  description: 'Build complex multi-step workflows effortlessly',
                  gradient: 'from-purple-500 to-pink-500'
                },
                {
                  icon: Users,
                  title: 'Hybrid Networks',
                  description: 'Combine AI agents, human approvals, and decision flows seamlessly',
                  gradient: 'from-orange-500 to-red-500'
                }
              ].map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="p-8 border-2 border-gray-200 rounded-xl hover:shadow-xl hover:border-gray-300 transition-all"
                  >
                    <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${feature.gradient} mb-6`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section id="capabilities" className="py-20 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">
                Everything You Need to Build Agentic Workflows
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Abstract away the complexity, focus on your business logic
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: FolderInput,
                  title: 'Data Integration',
                  description: 'Start with file inputs and send outputs to storage, messaging, or email'
                },
                {
                  icon: Brain,
                  title: 'AI Intelligence',
                  description: 'Leverage advanced AI models for intelligent decision-making'
                },
                {
                  icon: UserCheck,
                  title: 'Human-in-Loop',
                  description: 'Add human approvals and sign-offs at any step'
                },
                {
                  icon: GitBranch,
                  title: 'Decision Flows',
                  description: 'Create complex branching logic with conditional decisions'
                },
                {
                  icon: Clock,
                  title: 'Wait States',
                  description: 'Add delays and scheduled execution to your workflows'
                },
                {
                  icon: Zap,
                  title: 'Full Automation',
                  description: 'Execute production-ready workflows with one click'
                }
              ].map((capability, index) => {
                const Icon = capability.icon;
                return (
                  <div
                    key={index}
                    className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-3 rounded-lg shrink-0">
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">{capability.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{capability.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-20 px-6 bg-white">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">
                From Idea to Production in Minutes
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Building complex workflows has never been easier
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                {[
                  {
                    number: '01',
                    title: 'Describe Your Workflow',
                    description: 'Use natural language to describe what you want your workflow to do. No coding required.'
                  },
                  {
                    number: '02',
                    title: 'AI Builds Your Network',
                    description: 'Our AI interprets your prompt and constructs the state machine, decision flows, and integrations.'
                  },
                  {
                    number: '03',
                    title: 'Test & Refine',
                    description: 'Run simulations, test different scenarios, and refine your workflow with simple prompts.'
                  },
                  {
                    number: '04',
                    title: 'Execute in Production',
                    description: 'One-click execution to production with monitoring, logging, and automatic scaling.'
                  }
                ].map((step, index) => (
                  <div key={index} className="flex gap-6">
                    <div className="shrink-0">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white text-xl font-bold">
                        {step.number}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-2">{step.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur-3xl opacity-20"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="/images/photo-2.jpeg" 
                    alt="AI Technology"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600">
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto text-center text-white space-y-8">
              <h2 className="text-4xl lg:text-5xl font-bold">
                Ready to Transform Your Workflows?
              </h2>
              <p className="text-xl text-blue-100 leading-relaxed">
                Join thousands of teams already building intelligent agentic networks with StepFlow AI. 
                Start your free trial today and see the difference prompt-driven development can make.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <button
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 hover:bg-gray-100 text-lg font-semibold rounded-lg transition-all"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
                <button className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white/10 text-lg font-medium rounded-lg transition-all">
                  Schedule Demo
                </button>
              </div>
              <p className="text-sm text-blue-100 pt-4">
                No credit card required • 14-day free trial • Cancel anytime
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 px-6">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-2 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <span className="text-lg font-bold text-white">
                  StepFlow AI
                </span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                Building the future of intelligent workflow automation with prompt-driven development.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#capabilities" className="hover:text-white transition-colors">Capabilities</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API Reference</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Tutorials</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>© 2026 StepFlow AI. Part of AutoSynth Studios Platform.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
